import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import i18n, { LANGUAGE_STORAGE_KEY, ensureArResources } from '../../i18n';
// axios + axiosConfig are dynamic-imported inside the useEffect below so the
// landing page's main bundle doesn't carry ~36 KB of axios that won't run
// until the user does an API-touching action. axiosConfig itself was already
// lazy-only for the Redux store; this completes the deferral.

const LanguageContext = createContext({
  language: 'en',
  direction: 'ltr',
  setLanguage: () => {},
});

const normalizeLanguage = (value) => {
  if (!value) return 'en';
  const trimmed = value.toString().toLowerCase();
  if (trimmed.startsWith('ar')) return 'ar';
  return 'en';
};

let momentInstancePromise;
// Lazily load moment + locale data to keep it out of the initial bundle.
const ensureMoment = async () => {
  if (!momentInstancePromise) {
    momentInstancePromise = import('moment').then((mod) => mod.default || mod);
  }
  return momentInstancePromise;
};

const ensureMomentLocale = async (locale) => {
  const momentLib = await ensureMoment();
  if (locale === 'ar') {
    await import('moment/locale/ar');
  }
  return momentLib;
};

const detectInitialLanguage = () => {
  if (typeof window === 'undefined') return 'en';
  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored) return normalizeLanguage(stored);
  } catch (err) {
    // Ignore storage errors
  }

  const detected = i18n.language || window.navigator.language || 'en';
  return normalizeLanguage(detected);
};

const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(detectInitialLanguage);
  const direction = language === 'ar' ? 'rtl' : 'ltr';

  useEffect(() => {
    const targetLanguage = normalizeLanguage(language);
    // AR locale is dynamically loaded — wait for its bundle before switching
    // so users don't briefly see English strings on /ar/.
    if (targetLanguage === 'ar') {
      ensureArResources()
        .then(() => i18n.changeLanguage('ar'))
        .catch(() => i18n.changeLanguage('ar')); // best-effort
    } else {
      i18n.changeLanguage(targetLanguage);
    }

    try {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, targetLanguage);
    } catch (err) {
      // Ignore storage errors
    }

    if (typeof document !== 'undefined') {
      document.documentElement.lang = targetLanguage;
      document.documentElement.dir = direction;
    }

    let cancelled = false;
    const syncMomentLocale = async () => {
      if (targetLanguage === 'en' && !momentInstancePromise) {
        return;
      }
      const momentLib = await ensureMomentLocale(targetLanguage);
      if (!cancelled) {
        momentLib.locale(targetLanguage);
      }
    };

    syncMomentLocale().catch(() => {});

    // Defer axios + axiosConfig imports. They only matter for API-touching
    // actions, none of which run during landing's first paint. The fire-and-
    // forget dynamic imports let webpack split them into a chunk that's only
    // requested after hydration.
    Promise.all([
      import('axios'),
      import('../../utils/axiosConfig'),
    ])
      .then(([axiosMod, axiosCfgMod]) => {
        if (cancelled) return;
        axiosMod.default.defaults.headers.common['Accept-Language'] = targetLanguage;
        axiosCfgMod.default.defaults.headers.common['Accept-Language'] = targetLanguage;
      })
      .catch(() => {
        // axios sync is best-effort; failing here doesn't block i18n
      });

    return () => {
      cancelled = true;
    };
  }, [language, direction]);

  const setLanguage = (nextLanguage) => {
    setLanguageState(normalizeLanguage(nextLanguage));
  };

  const value = useMemo(
    () => ({
      language,
      direction,
      setLanguage,
    }),
    [language, direction],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);

export default LanguageProvider;
